import React, { useEffect, useRef } from 'react'
import { icon } from '@fortawesome/fontawesome-svg-core'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { genericError } from './useRecord'

// PayPal / Venmo SDK button. Adapted from donate_v6/fiat/Summary's button
// effect, but kept mounted across tip / cover-costs / amount changes:
// stateRef reads the current values when the donor clicks, which is when
// the draft is saved and the PayPal order's amounts are fixed. The SDK
// itself comes from usePaypalSdk so the same instance services both the
// venmo eligibility probe and the rendered button — see usePaypalSdk for why.
const PaypalButton = ({
  i18n, donation, paypal, paypalConfig, partnerItem, paymentMethod, paypalSdk,
}) => {
  const buttonRef = useRef()

  const stateRef = useRef()
  stateRef.current = {
    total: donation.total,
    tip: donation.tip,
    currency: donation.currency.iso_code,
    partnerItemId: partnerItem.id,
    validateTip: donation.validateTip,
  }

  // The SDK keeps the callbacks it was handed when the buttons were built,
  // and a tip or cover-costs change doesn't rebuild them. paypal.saveDraft
  // saves the donation of the render it came from, so calling the one
  // captured at build time would record the old amounts while PayPal
  // charged the current total. The callbacks go through this ref to reach
  // the latest render's handlers instead.
  const paypalRef = useRef()
  paypalRef.current = paypal

  const isSubscription = donation.state.subscription !== 'one_time'

  useEffect(() => {
    let buttons

    const clearButton = () => {
      if (!buttonRef.current) return
      while (buttonRef.current.firstChild)
        buttonRef.current.removeChild(buttonRef.current.firstChild)
    }

    const insertSpinner = () => {
      const spinner = icon(faCircleNotch, { classes: 'fa-2x fa-spin' })
      buttonRef.current?.insertAdjacentHTML(
        'beforeend',
        `<div class="text-center" style="color: #0079c1;">${spinner.html}</div>`
      )
    }

    clearButton()

    if (!paypalSdk || !paypal.ready) {
      insertSpinner()
      return
    }

    // The donation as of the click, and the draft saved from it. The PayPal
    // order charges these amounts rather than stateRef's at createOrder
    // time, so it can't differ from what the draft recorded.
    let clicked

    // PayPal SDK calls this before opening the popup, and holds off on
    // createOrder / createVaultSetupToken until it settles. actions.reject()
    // surfaces no PayPal-side UI, leaving the OrderSummary's inline error
    // (set by validateTip, or by saveDraft) as the only feedback.
    const onClick = async (data, actions) => {
      const state = stateRef.current
      if (!state.validateTip()) return actions.reject()

      const draftToken = await paypalRef.current.saveDraft()
      if (!draftToken) return actions.reject()

      clicked = { ...state, draftToken }
      return actions.resolve()
    }

    // paymentToken is the vault token on the recurring path. With it,
    // PaypalController#create charges the first month on the spot; without
    // it, the order waits for a buyer approval nothing ever asks for.
    const createOrder = async paymentToken => {
      const { total, tip, currency, partnerItemId, draftToken } = clicked
      const response = await fetch(paypalConfig.create_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          tip,
          currency,
          partner_item_id: partnerItemId,
          payment_token: paymentToken,
          order_id: draftToken,
        }),
      })
      if (!response.ok) throw new Error('Failed to create PayPal order')
      const { id } = await response.json()
      return id
    }

    const createVaultSetupToken = async () => {
      const response = await fetch(paypalConfig.vault_setup_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) throw new Error('Failed to create PayPal vault setup token')
      const { id } = await response.json()
      return id
    }

    const onApproveVault = async data => {
      // The first month is charged below, so the panel is covered from here.
      // process() clears it on its own failures; this clears it on the vault
      // token's or the charge's.
      paypalRef.current.startCharge()

      let result, orderId
      try {
        const response = await fetch(paypalConfig.vault_approve_path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vaultSetupToken: data.vaultSetupToken }),
        })
        if (!response.ok) throw new Error('Failed to approve PayPal vault')
        result = await response.json()

        orderId = await createOrder(result.id)
      } catch (error) {
        console.error('PayPal error:', error)
        return paypalRef.current.fail(genericError(i18n))
      }

      return paypalRef.current.process({
        orderID: orderId,
        paymentToken: result.id,
        customerId: result.customer?.id,
      })
    }

    const onError = error => {
      console.error('PayPal error:', error)
      paypalRef.current.fail(genericError(i18n))
    }

    const fundingSource = paypalSdk.FUNDING[paymentMethod.toUpperCase()]

    // Match v7's regular .btn: 44px tall, 8px radius. PayPal accepts
    // borderRadius in pixels; height clamps within 25-55.
    const style = { height: 44, borderRadius: 8 }

    buttons = isSubscription
      ? paypalSdk.Buttons({
        fundingSource,
        style,
        onClick,
        createVaultSetupToken,
        onApprove: onApproveVault,
        onError,
      })
      : paypalSdk.Buttons({
        fundingSource,
        style,
        onClick,
        // The SDK calls this with (data, actions), which mustn't reach
        // createOrder as a vault token.
        createOrder: () => createOrder(),
        onApprove: data => paypalRef.current.process(data),
        onError,
      })

    buttons.render(buttonRef.current)

    return () => buttons?.close()
  }, [ paypalSdk, paypal.ready, paymentMethod, isSubscription ])

  return <div ref={buttonRef} className="grid" />
}

export default PaypalButton
